import type { Role, User } from "@nova-ai/shared"
import { AtSign, CalendarDays, ShieldCheck, UsersRound } from "lucide-react"

import { formatDateTime } from "../../lib/format-date"
import { StatusBadge } from "../ui/status-badge"
import { UserRoleSelect } from "./user-role-select"

type UsersTableProps = {
  users: readonly User[]
  isUpdating: boolean
  onRoleChange: (id: number, role: Role) => void
}

export function UsersTable({ users, isUpdating, onRoleChange }: UsersTableProps) {
  const activeCount = users.filter((user) => user.active).length
  const adminCount = users.filter((user) => user.role === "admin").length

  return (
    <section className="overflow-hidden rounded-xl border border-border bg-card shadow-card">
      <header className="flex flex-col gap-4 border-b border-border px-5 py-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="flex size-10 items-center justify-center rounded-xl bg-foreground text-background">
            <UsersRound className="size-4.5" />
          </span>

          <div>
            <h2 className="text-[15px] font-semibold tracking-tight text-foreground">
              Operator directory
            </h2>

            <p className="mt-0.5 text-xs text-muted-foreground">
              Identity, account state, and access level in one place.
            </p>
          </div>
        </div>

        <dl className="flex items-center gap-5 text-xs">
          <DirectoryMetric label="People" value={users.length} />

          <DirectoryMetric label="Active" value={activeCount} tone="success" />

          <DirectoryMetric label="Admins" value={adminCount} />
        </dl>
      </header>

      <div className="hidden grid-cols-[minmax(0,1fr)_150px_170px] gap-5 border-b border-border/70 bg-muted/25 px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.09em] text-muted-foreground lg:grid">
        <span>Person</span>

        <span>Access</span>

        <span>Joined</span>
      </div>

      <div className="divide-y divide-border/70">
        {users.map((user, index) => (
          <div
            key={user.id}
            className="motion-list-row grid gap-4 px-5 py-4 transition-colors duration-200 hover:bg-info/[0.035] lg:grid-cols-[minmax(0,1fr)_150px_170px] lg:items-center lg:gap-5"
            style={{ animationDelay: `${Math.min(index, 7) * 28}ms` }}
          >
            <div className="flex min-w-0 items-center gap-3">
              <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold tracking-wide text-foreground ring-1 ring-border">
                {initials(user)}
              </span>

              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="truncate text-sm font-medium text-foreground">
                    {user.name ?? user.email ?? "Unnamed operator"}
                  </span>

                  <StatusBadge tone={user.active ? "success" : "muted"} size="sm">
                    {user.active ? "Active" : "Inactive"}
                  </StatusBadge>
                </div>

                <div className="mt-1 flex items-center gap-1.5 truncate text-xs text-muted-foreground">
                  <AtSign className="size-3 shrink-0" />

                  <span className="truncate">{user.email ?? "No email"}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <ShieldCheck className="size-3.5 text-muted-foreground" />

              <UserRoleSelect
                role={user.role}
                disabled={isUpdating}
                onChange={(role) => onRoleChange(user.id, role)}
              />
            </div>

            <time
              dateTime={user.createdAt}
              title={user.createdAt}
              className="flex items-center gap-2 whitespace-nowrap text-xs tabular-nums text-muted-foreground"
            >
              <CalendarDays className="size-3.5" />

              {formatDateTime(user.createdAt)}
            </time>
          </div>
        ))}
      </div>
    </section>
  )
}

function DirectoryMetric({
  label,
  value,
  tone = "default",
}: {
  label: string
  value: number
  tone?: "default" | "success"
}) {
  return (
    <div>
      <dt className="text-muted-foreground">{label}</dt>

      <dd
        className={
          tone === "success"
            ? "mt-0.5 text-lg font-semibold tabular-nums text-success"
            : "mt-0.5 text-lg font-semibold tabular-nums text-foreground"
        }
      >
        {value.toLocaleString()}
      </dd>
    </div>
  )
}

function initials(user: User): string {
  const source =
    [user.name?.trim(), user.email?.split("@")[0]].find((value) => Boolean(value)) ?? "?"
  const parts = source.split(/[\s._-]+/).filter(Boolean)
  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("")
}
